import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect, useMemo } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import * as styles from './styles'; // Adjust path if needed
import { BiX } from 'react-icons/bi';
import { Loader2 } from 'src/components/common/icons';
import { Field, DateField } from './Master'; // Imported standard components
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

// --- Interfaces ---
export interface IInvoiceSearchResult {
    clientLease: string;
    invoiceNo: string;
    lsNo: number;
    fmtLeaseNumber: string;
    kind: string;
    clNo: number;
    prNo: number;
    bonus: number;
    rental: number;
    bank: number;
    fwdBank: number;
    recordingFees: number;
    other: number;
    lessor: string;
    liTg: number;
    invDate: string;
    invoiceTotal: number;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    initialClNo?: number;
    initialPrNo?: number;
    onSuccess: (newGroupNo: number) => void;
}

const CreateInvoiceGroupModal: React.FC<IProps> = ({ isOpen, onClose, initialClNo, initialPrNo, onSuccess }) => {
    const { get, post, response, loading } = useFetch();

    // --- Search State ---
    const [clNo, setClNo] = useState<string>('');
    const [prNo, setPrNo] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    // --- Grid State ---
    const [results, setResults] = useState<IInvoiceSearchResult[]>([]);
    const [selectedLiTgs, setSelectedLiTgs] = useState<number[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [workflowError, setWorkflowError] = useState<ILeaseRecordsApiError | null>(null);

    // --- Initialize Defaults ---
    useEffect(() => {
        if (isOpen) {
            if (initialClNo) {
                setClNo(initialClNo.toString());
            }
            if (initialPrNo) {
                setPrNo(initialPrNo.toString());
            }

            // --- Pre-fill Dates (1 Year Ago -> Today) ---
            const end = new Date();
            const start = new Date();
            start.setFullYear(end.getFullYear() - 1);

            setEndDate(end.toISOString().split('T')[0]);
            setStartDate(start.toISOString().split('T')[0]);
        } else {
            // Reset state on close
            setResults([]);
            setSelectedLiTgs([]);
            setHasSearched(false);
        }
    }, [isOpen, initialClNo, initialPrNo]);

    // --- Computed Total ---
    const totalAmount = useMemo(() => {
        return results.filter((r) => selectedLiTgs.includes(r.liTg)).reduce((sum, r) => sum + (r.invoiceTotal || 0), 0);
    }, [results, selectedLiTgs]);

    // --- Handlers ---

    const handleQuery = async () => {
        if (!clNo || !prNo || !startDate || !endDate) {
            alert('Please fill in all search fields (Client No, Project No, Begin Date, End Date).');
            return;
        }

        setWorkflowError(null);
        const data = await get(`/v1.0/leases/invoice-groups/search?clNo=${clNo}&prNo=${prNo}&start=${startDate}&end=${endDate}`);

        if (response.ok) {
            setResults(data);
            setSelectedLiTgs([]); // Reset selection on new search
            setHasSearched(true);
        } else {
            setWorkflowError(
                parseLeaseRecordsApiError(data || response.data || { title: 'Error querying invoices. Please check your inputs.' }, response.status)
            );
        }
    };

    const handleCreate = async () => {
        if (selectedLiTgs.length === 0) {
            setWorkflowError(parseLeaseRecordsApiError({ title: 'Select at least one invoice to create a group.' }));
            return;
        }

        // The endpoint expects { SelectedLiTgs: [1, 2, 3] }
        const payload = { selectedLiTgs: selectedLiTgs };

        setWorkflowError(null);
        const result = await post('/v1.0/leases/invoice-groups/create', payload);

        if (response.ok) {
            // Success: result is the new GroupNo (int)
            onSuccess(result);
        } else {
            // Error: result is likely the error message string or object
            setWorkflowError(parseLeaseRecordsApiError(result || response.data, response.status));
        }
    };

    // --- Selection Logic ---

    const toggleRow = (liTg: number) => {
        setSelectedLiTgs((prev) => (prev.includes(liTg) ? prev.filter((id) => id !== liTg) : [...prev, liTg]));
    };

    const selectAll = () => {
        setSelectedLiTgs(results.map((r) => r.liTg));
    };

    const deselectAll = () => {
        setSelectedLiTgs([]);
    };

    // --- Render Helpers ---
    const formatMoney = (val?: number) => (val ? `$${val.toFixed(2)}` : '');

    if (!isOpen) {
        return null;
    }

    return (
        <div style={modalStyles.modalOverlay}>
            <div style={{ ...modalStyles.modalContent, width: '1450px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <LeaseRecordsErrorAlert error={workflowError} onDismiss={() => setWorkflowError(null)} />
                <div style={modalStyles.modalHeader}>
                    <h3 style={modalStyles.modalTitle}>Create Invoice Group</h3>
                    <button onClick={onClose} style={modalStyles.iconButton}>
                        <BiX size={24} />
                    </button>
                </div>

                {/* Search Bar */}
                <div style={{ padding: '1rem', backgroundColor: styles.colors.gray100, borderBottom: `1px solid ${styles.colors.border}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '100px 100px 140px 140px auto', gap: '1rem', alignItems: 'end' }}>
                        <div>
                            <Field label="Client No." value={clNo} editable={true} onChange={setClNo} type="number" />
                        </div>
                        <div>
                            <Field label="Project No." editable={true} value={prNo} onChange={setPrNo} type="number" />
                        </div>
                        <div>
                            <DateField label="Begin Date" value={startDate} onChange={setStartDate} editable={true} />
                        </div>
                        <div>
                            <DateField label="End Date" value={endDate} onChange={setEndDate} editable={true} />
                        </div>
                        <div>
                            <button
                                onClick={handleQuery}
                                style={{
                                    ...styles.buttonSecondary,
                                    // Removed fixed height, using padding to match OK button
                                    minWidth: '80px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    marginBottom: '2px', // Visual alignment with Field inputs
                                }}
                                disabled={loading}
                            >
                                {loading ? <Loader2 style={{ animation: 'spin 1s linear infinite', width: 16, height: 16 }} /> : 'Query'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Grid */}
                <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: '1000px' }}>
                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: styles.colors.gray100,
                                zIndex: 2,
                                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)',
                            }}
                        >
                            <tr>
                                <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                                    <LeaseRecordInput
                                        type="checkbox"
                                        checked={results.length > 0 && selectedLiTgs.length === results.length}
                                        onChange={(e) => (e.target.checked ? selectAll() : deselectAll())}
                                    />
                                </th>
                                <th style={thStyle}>Client Doc</th>
                                <th style={thStyle}>Inv #</th>
                                <th style={thStyle}>Doc Number</th>
                                <th style={thStyle}>Prospect #</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Bonus</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Rental</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Bank Fees</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Rec. Fees</th>
                                <th style={thStyle}>Inv Date</th>
                                <th style={thStyle}>Lessor</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.length === 0 && hasSearched ? (
                                <tr>
                                    <td colSpan={12} style={{ padding: '2rem', textAlign: 'center', color: styles.colors.gray500 }}>
                                        No invoices found matching criteria.
                                    </td>
                                </tr>
                            ) : (
                                results.map((r, i) => (
                                    <tr
                                        key={r.liTg}
                                        style={{
                                            borderBottom: `1px solid ${styles.colors.border}`,
                                            backgroundColor: selectedLiTgs.includes(r.liTg) ? '#EFF6FF' : i % 2 === 0 ? '#ffffff' : '#F9FAFB',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => toggleRow(r.liTg)}
                                    >
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <LeaseRecordInput
                                                type="checkbox"
                                                checked={selectedLiTgs.includes(r.liTg)}
                                                onChange={() => {
                                                    /* Handled by tr onClick */
                                                }}
                                                onClick={(e) => e.stopPropagation()} // Prevent double toggle
                                                onChangeCapture={() => toggleRow(r.liTg)}
                                            />
                                        </td>
                                        <td style={tdStyle}>{r.clientLease}</td>
                                        <td style={tdStyle}>{r.invoiceNo}</td>
                                        <td style={tdStyle}>{r.lsNo}</td>
                                        <td style={tdStyle}>
                                            {r.clNo}-{r.prNo}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.bonus)}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.rental)}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.bank)}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.recordingFees)}</td>
                                        <td style={tdStyle}>{new Date(r.invDate).toLocaleDateString()}</td>
                                        <td style={tdStyle}>{r.lessor}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{formatMoney(r.invoiceTotal)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '1rem',
                        backgroundColor: '#fff',
                        borderTop: `1px solid ${styles.colors.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Left Actions */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={handleCreate} style={styles.buttonPrimary} disabled={loading || results.length === 0}>
                            {loading ? 'Processing...' : 'OK'}
                        </button>
                        <button onClick={selectAll} style={styles.buttonSecondary} disabled={results.length === 0}>
                            Select All
                        </button>
                        <button onClick={deselectAll} style={styles.buttonSecondary} disabled={results.length === 0}>
                            Deselect All
                        </button>
                        <button onClick={onClose} style={styles.buttonSecondary}>
                            Cancel
                        </button>
                    </div>

                    {/* Right Total */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1rem', fontWeight: 600, color: styles.colors.gray700 }}>Invoice Total:</span>
                        <div
                            style={{
                                backgroundColor: '#10B981',
                                color: 'white',
                                padding: '0.4rem 1rem',
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                minWidth: '120px',
                                textAlign: 'right',
                            }}
                        >
                            ${totalAmount.toFixed(2)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Local Styles ---
const thStyle: React.CSSProperties = {
    padding: '0.6rem 0.5rem',
    fontWeight: 600,
    color: '#374151',
    borderBottom: '2px solid #D1D5DB',
    whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
    padding: '0.5rem',
    color: '#111827',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '150px',
};

// --- Styles ---
export const modalStyles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(3px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        width: '90%',
        maxWidth: '1200px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        overflow: 'hidden',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.25rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#111827',
        margin: 0,
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: '1rem',
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#fff',
    },
    iconButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#6B7280',
    },
};

export default CreateInvoiceGroupModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/CreateInvoiceGroupModal.tsx